import type { User } from '~/server/types';

/**
 * Whether the signed-in member has opted out of the sale/sharing of their personal
 * information. Guests are never opted out here — their equivalent signal is GPC.
 *
 * Read off the root-data user, which is served from the Redis user cache, so a change
 * made in the dashboard takes up to the cache TTL to suppress pixels for that member.
 */
export const hasSaleOrSharingOptOut = (
    user: User | null | undefined,
): boolean =>
    user?.__typename === 'Me' && user.OptedOutOfSaleOrSharing === true;

/**
 * Shared gate for every tracker that shares personal data with a third party:
 * the advertising pixels (Meta, Pinterest, LinkedIn, Outbrain) and the
 * analytics trackers (GA4, GTM, Snitcher). Two signals suppress them:
 *
 * - GPC, a per-browser hint, which only counts in markets configured to honour it.
 * - An explicit opt-out of the sale/sharing of personal information, which counts in
 *   every market because it is a choice the person made rather than a browser
 *   default. It arrives from either source: the first-party cookie set on
 *   `/your-privacy-choices`, or the signed-in member's dashboard preference. Callers
 *   pass the OR of whichever signals they can see.
 */
export const shouldAllowThirdPartySharing = (
    honourGlobalPrivacyControl: boolean,
    globalPrivacyControl: boolean,
    explicitSaleOrSharingOptOut = false,
): boolean => {
    if (explicitSaleOrSharingOptOut) {
        return false;
    }
    if (!honourGlobalPrivacyControl) {
        return true;
    }
    return !globalPrivacyControl;
};

/**
 * Reads the HTTP-layer Global Privacy Control signal from incoming request
 * headers (`Sec-GPC: 1`). Used during SSR where `navigator.globalPrivacyControl`
 * is unavailable.
 */
export const readGlobalPrivacyControlHeader = (
    headers: Record<string, string | string[] | undefined>,
): boolean => {
    const raw = headers['sec-gpc'];
    const value = Array.isArray(raw) ? raw[0] : raw;
    return String(value ?? '').trim() === '1';
};
